import { html, nothing } from '@shared/utils/rendering/html';
import { useState, useEffect, useMemo, useRef } from 'preact/hooks';
import { useFeatureChecks } from '@core/utils/features/feature-context';
import { useNavigate } from '@core/utils/context/navigation-context';
import { toast } from 'react-toastify';
import ProgressCard from '@core/components/shared/data-displays/progress-card';
import Button from '@shared/components/buttons';
import { Icon, IconNames } from '@shared/components/icons';
import ActionsDropdown from '@shared/components/dropdowns/actions-dropdown';
import { LoaderWithTitle } from '@shared/components/loading';
import AlertModal from '@shared/components/modals/alert-modal';
import AlertTypes from '@shared/components/modals/alert-modal/alert-types';
import OrgFilterGroup from '@core/components/shared/org-filters/org-filter-group';
import { RdarrRoutingUrls } from '@core/constants/routing/urls.constants';
import { SarbAdvancedFilters } from './filters/sarb-advanced-filters';
import { SarbQuestionsTable } from './questions-table/sarb-questions-table';
import { SarbCharts } from './charts';
import { ConsolidatedReportScreen } from './consolidated-report';
import { SarbExportModal } from './export/sarb-export-modal';
import { SarbSelfAssessmentService } from '@core/utils/api/sarb-self-assessment.service';
import { getRatingBand } from '@core/constants/chips/ratings.constants';
import { useCachedFetch } from '@core/utils/hooks/use-cached-fetch';
import { ORG_FILTERS_STORAGE_KEY } from '@core/constants/utility/org-filters-storage.constants';
import './index.scss';

const DEFAULT_PAGE_SIZE = 10;

// Centered icon/heading/description empty-state, matching the RDARR Self-Assessment tab's style.
const EmptyState = ({ icon, heading, description, button }) => html`
    <div class="text-center py-5">
        <${Icon} name=${icon} css="fs-1 text-muted d-block mb-2" />
        <h5 class="fw-semibold mb-2">${heading}</h5>
        <p class="text-muted mb-0">${description}</p>
        ${button ? html`<div class="mt-3">${button}</div>` : nothing}
    </div>
`;

const SarbSelfAssessment = ({ onFullScreen = () => {} }) => {
    const { useFeature } = useFeatureChecks();
    // Fail closed: this gates a destructive write action (overwrites manual ratings), so a
    // loading/errored feature fetch must hide it rather than re-enable it.
    const autoPopulateEnabled = useFeature('sarb.autoPopulate.enabled', false);
    const { onNavigate } = useNavigate();

    const [validEntities, setValidEntities] = useState([]);
    const [orgFilters, setOrgFilters] = useState({ legalEntity: null, period: null });
    const orgFiltersRestoredRef = useRef(false);
    const [filters, setFilters] = useState({ principle: '', rating: '', searchTerm: '' });
    const [pagination, setPagination] = useState({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });
    const [assessment, setAssessment] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showAutoPopulateModal, setShowAutoPopulateModal] = useState(false);
    const [showConsolidatedReport, setShowConsolidatedReport] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [sortState, setSortState] = useState({ column: 'sarbQuestionId', descending: false });

    const apiFilters = useMemo(
        () => ({
            legalEntityId: orgFilters?.legalEntity?.value,
            periodId: orgFilters?.period?.value,
            periodValid: orgFilters?.period?.valid,
        }),
        [orgFilters?.legalEntity, orgFilters?.period]
    );

    const [assessmentQueryOptions, setAssessmentQueryOptions] = useState({ enabled: false });

    useEffect(() => {
        const storedFilters = localStorage.getItem(ORG_FILTERS_STORAGE_KEY);
        if (storedFilters) {
            const parsedFilters = JSON.parse(storedFilters);
            if (parsedFilters.legalEntity?.value && validEntities?.length) {
                const isValidEntity = validEntities.find(
                    (e) => e.value === parsedFilters.legalEntity.value
                );
                if (!isValidEntity) parsedFilters.legalEntity = null;
            }
            if (!parsedFilters?.validUntilDate || parsedFilters.validUntilDate < Date.now()) {
                setOrgFilters((prev) => ({ ...prev, period: null, legalEntity: null }));
            } else {
                setOrgFilters(parsedFilters);
            }
        }
        // When there is exactly one entity it must always be auto-selected,
        // regardless of what localStorage held (overrides any restore above).
        if (validEntities?.length === 1) {
            setOrgFilters((prev) => ({ ...prev, legalEntity: validEntities[0] }));
        }
    }, [validEntities]);

    useEffect(() => {
        if (!validEntities?.length) return;
        if (orgFilters?.legalEntity?.value) {
            const isValidEntity = validEntities.find(
                (e) => e.value === orgFilters.legalEntity.value
            );
            if (!isValidEntity) setOrgFilters((prev) => ({ ...prev, legalEntity: null }));
        }
    }, [validEntities]);

    const { data: legalEntityAssessment, isLoading: isLegalEntityAssessmentLoading } =
        useCachedFetch('/legal-entity-assessments', apiFilters, assessmentQueryOptions);

    useEffect(() => {
        if (apiFilters?.legalEntityId && apiFilters.periodId && !assessmentQueryOptions.enabled) {
            setAssessmentQueryOptions({ enabled: true });
        }
        if (
            (!apiFilters?.legalEntityId || !apiFilters.periodId) &&
            assessmentQueryOptions.enabled
        ) {
            setAssessmentQueryOptions({ enabled: false });
        }
    }, [apiFilters?.legalEntityId, apiFilters.periodId, assessmentQueryOptions.enabled]);

    useEffect(() => {
        if (legalEntityAssessment?.legalEntityAssessmentId) {
            loadAssessment(legalEntityAssessment.legalEntityAssessmentId);
        } else {
            setAssessment(null);
            setQuestions([]);
        }
    }, [legalEntityAssessment?.legalEntityAssessmentId]);

    const loadAssessment = async (legalEntityAssessmentId) => {
        setIsLoading(true);
        try {
            let assessmentData =
                await SarbSelfAssessmentService.getAssessment(legalEntityAssessmentId);
            if (!assessmentData) {
                try {
                    assessmentData =
                        await SarbSelfAssessmentService.createAssessment(legalEntityAssessmentId);
                    toast.success(
                        `Created new SARB Self-Assessment with ${assessmentData?.totalQuestions ?? 0} questions`
                    );
                } catch (createError) {
                    if (createError.status === 404) {
                        toast.error(
                            'Legal Entity Assessment not found. Please create an assessment first.'
                        );
                        setAssessment(null);
                        setQuestions([]);
                        setIsLoading(false);
                        return;
                    }
                    throw createError;
                }
            }
            if (assessmentData?.sarbSelfAssessmentId) {
                setAssessment(assessmentData);
                setQuestions(assessmentData.questionResponses || []);
            } else {
                throw new Error('Invalid assessment data received');
            }
        } catch (error) {
            toast.error(
                error.data?.detail ||
                    error.data?.message ||
                    error.message ||
                    'Failed to load assessment'
            );
            setAssessment(null);
            setQuestions([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAutoPopulateConfirm = async () => {
        setShowAutoPopulateModal(false);
        setIsLoading(true);
        try {
            const updatedAssessment = await SarbSelfAssessmentService.autoPopulateFromEvidence(
                assessment.sarbSelfAssessmentId
            );
            setAssessment(updatedAssessment);
            setQuestions(updatedAssessment.questionResponses || []);
            toast.success('Assessment auto-populated from Evidence Based Approach');
        } catch {
            toast.error('Failed to auto-populate assessment');
        } finally {
            setIsLoading(false);
        }
    };

    // Save org filters to localStorage — skip the initial mount render so we don't overwrite
    // a stored selection (shared with the RDARR tab) before the read effect above restores it.
    useEffect(() => {
        if (!orgFiltersRestoredRef.current) {
            orgFiltersRestoredRef.current = true;
            return;
        }
        const validUntilDate = new Date();
        validUntilDate.setMonth(validUntilDate.getMonth() + 1);
        localStorage.setItem(
            ORG_FILTERS_STORAGE_KEY,
            JSON.stringify({ ...orgFilters, validUntilDate })
        );
    }, [orgFilters]);

    // Client-side filtering
    const filteredQuestions = useMemo(() => {
        return questions.filter((q) => {
            // The 11 principle summary rows (Q16, Q23, ... Q86) are shown in the grid alongside the
            // real questions - their rating is the computed roll-up of the rows above them in the
            // same principle. They are read-only (the grid has no edit path) and go through the same
            // principle/rating/search filters below. They remain excluded from the overall SARB
            // rating and the consolidated report on the server.

            // principleNumber is the serialised Principle enum integer
            if (filters.principle && q.principleNumber?.toString() !== filters.principle)
                return false;
            // Rating is a decimal (e.g. 3.6) and no longer a whole number, so an exact string
            // match against the filter's band value ('0'-'4') would never match - compare against
            // the value's colour band instead (getRatingBand), which is what the filter options
            // and the rating chips both represent. Null/unrated falls into the "Not Assessed" (0) band.
            if (filters.rating && getRatingBand(q.rating).value.toString() !== filters.rating)
                return false;
            if (filters.searchTerm) {
                const s = filters.searchTerm.toLowerCase();
                return (
                    q.questionText?.toLowerCase().includes(s) ||
                    q.principleName?.toLowerCase().includes(s) ||
                    q.paragraph?.toLowerCase().includes(s)
                );
            }
            return true;
        });
    }, [questions, filters.principle, filters.rating, filters.searchTerm]);

    // Client-side sorting
    const sortedQuestions = useMemo(() => {
        const { column, descending } = sortState;
        return [...filteredQuestions].sort((a, b) => {
            const aVal = a[column] ?? '';
            const bVal = b[column] ?? '';
            const cmp =
                typeof aVal === 'number' && typeof bVal === 'number'
                    ? aVal - bVal
                    : String(aVal).localeCompare(String(bVal));
            return descending ? -cmp : cmp;
        });
    }, [filteredQuestions, sortState]);

    const handleSortChange = (sortData) => {
        if (!sortData) return;
        setPagination((prev) => ({ ...prev, pageNumber: 1 }));
        setSortState({ column: sortData.sortByColumn, descending: sortData.sortByDescending });
    };

    // Reset to page 1 when filters change
    useEffect(() => {
        setPagination((prev) => ({ ...prev, pageNumber: 1 }));
    }, [filters.principle, filters.rating, filters.searchTerm]);

    // Current page of rows for Venus Table
    const pagedRows = useMemo(() => {
        const start = (pagination.pageNumber - 1) * pagination.pageSize;
        return sortedQuestions.slice(start, start + pagination.pageSize);
    }, [sortedQuestions, pagination.pageNumber, pagination.pageSize]);

    const handlePaginationEvent = ({ pageNumber, pageSize }) => {
        setPagination({ pageNumber, pageSize });
    };

    // Overall self-assessment rating average, computed server-side by SarbSelfAssessmentOutputModel
    // (Not Assessed counts as 0 and is included in the denominator).
    const overallRating = useMemo(() => Number(assessment?.overallRating ?? 0), [assessment]);

    const handleShowConsolidated = (show) => {
        setShowConsolidatedReport(show);
        onFullScreen(show);
    };

    // Named handlers — inline arrow functions in onClick cause unnecessary re-renders
    const handleOpenAutoPopulate = () => setShowAutoPopulateModal(true);
    const handleCloseAutoPopulate = () => setShowAutoPopulateModal(false);
    const handleOpenConsolidated = () => handleShowConsolidated(true);
    const handleCloseConsolidated = () => handleShowConsolidated(false);
    const handleOpenExport = () => setShowExportModal(true);
    const handleCloseExport = () => setShowExportModal(false);
    const handleFilterChange = (f) => setFilters((prev) => ({ ...prev, ...f }));
    const handleGoToRdarrSelfAssessment = () => onNavigate(RdarrRoutingUrls.evidenceBasedApproach);

    if (showConsolidatedReport) {
        return html`
            <${ConsolidatedReportScreen}
                onBack=${handleCloseConsolidated}
                orgFilters=${orgFilters}
            />
        `;
    }

    const actionsOptions = [
        ...(questions.length && autoPopulateEnabled
            ? [
                  {
                      onClickEvent: handleOpenAutoPopulate,
                      label: {
                          icon: html`<${Icon} name="smart_toy" />`,
                          text: 'Auto-Populate Evidence',
                      },
                  },
              ]
            : []),
        {
            onClickEvent: handleOpenConsolidated,
            label: { icon: html`<${Icon} name="finance" />`, text: 'Consolidated Report' },
            disabled: !assessment?.sarbSelfAssessmentId,
        },
        {
            onClickEvent: handleOpenExport,
            label: { icon: html`<${Icon} name=${IconNames.OpenInNew} />`, text: 'Export' },
            disabled: !assessment?.sarbSelfAssessmentId,
        },
    ];

    const orgFilterActions = html`
        <${ActionsDropdown} id="sarb-actions-menu" options=${actionsOptions} />
    `;

    return html`
        <${OrgFilterGroup}
            orgFilters=${orgFilters}
            setOrgFilters=${setOrgFilters}
            setValidEntities=${setValidEntities}
            actions=${orgFilterActions}
        />

        ${
            isLegalEntityAssessmentLoading || isLoading
                ? html`<${LoaderWithTitle}
                      busyLoading=${true}
                      title="Loading SARB Self-Assessment..."
                  />`
                : !orgFilters?.legalEntity || !orgFilters?.period
                  ? html`
                        <${EmptyState}
                            icon=${IconNames.Info}
                            heading="Select Legal Entity and Period"
                            description="Please select a Legal Entity and Period above to view the SARB Self-Assessment."
                        />
                    `
                  : !legalEntityAssessment
                    ? html`
                          <${EmptyState}
                              icon=${IconNames.Warning}
                              heading="No Assessment Found"
                              description="No Legal Entity Assessment exists for the selected period. Please create one in the Evidence Based Approach tab first."
                          />
                      `
                    : !assessment
                      ? html`
                            <${EmptyState}
                                icon=${IconNames.Calendar_Month}
                                heading="No Assessment Started"
                                description="A new RDARR Self-Assessment must be started for this reporting period."
                                button=${html`<${Button} buttonType="secondary" css="btn-sm" label=${{ precedent: html`<${Icon} name="lock_open" />`, text: 'GO TO RDARR Self-Assessment' }} onClick=${handleGoToRdarrSelfAssessment} />`}
                            />
                        `
                      : html`
                            <div class="sarb-self-assessment-container">
                                <!-- Overall rating card(s) — same placement/style as the RDARR Self-Assessment tab -->
                                <div class="flex-wrap-4 mb-3">
                                    <${ProgressCard}
                                        title=${{ text: 'Self-Assessment Rating' }}
                                        progress=${{
                                            value: overallRating,
                                            decimalPlaces: 1,
                                            unit: '',
                                        }}
                                        css="flex-item"
                                    />
                                </div>

                                <!-- Ratings card: filters + table -->
                                <div class="card mb-3">
                                    <div class="card-header bg-white sarb-card-header">
                                        <!-- Row 1: heading -->
                                        <div class="sarb-rating-row">
                                            <h5 class="mb-0 fw-semibold me-2">Ratings</h5>
                                        </div>
                                        <!-- Row 2: search / filter controls -->
                                        <div class="sarb-filter-row">
                                            <${SarbAdvancedFilters}
                                                onFilterChange=${handleFilterChange}
                                            />
                                        </div>
                                    </div>

                                    <${SarbQuestionsTable}
                                        rows=${pagedRows}
                                        total=${sortedQuestions.length}
                                        isLoading=${isLoading}
                                        paginationData=${{ pageNumber: pagination.pageNumber, pageSize: pagination.pageSize, total: sortedQuestions.length }}
                                        onPaginate=${handlePaginationEvent}
                                        onSortChange=${handleSortChange}
                                    />
                                </div>

                                <!-- Charts below the table -->
                                <${SarbCharts}
                                    assessmentId=${assessment.sarbSelfAssessmentId}
                                    isAssessmentLoading=${false}
                                    activeFilters=${filters}
                                />

                                <!-- Export modal -->
                                <${SarbExportModal}
                                    isVisible=${showExportModal}
                                    onClose=${handleCloseExport}
                                    questions=${questions}
                                    assessmentId=${assessment?.sarbSelfAssessmentId}
                                    orgFilters=${orgFilters}
                                />

                                <!-- Auto-populate confirmation modal -->
                                <${AlertModal}
                                    id="auto-populate-warning"
                                    header=${{ title: { text: 'Auto-Populate from Evidence' } }}
                                    footer=${html`
                                        <${Button}
                                            buttonType="secondary"
                                            label=${{ text: 'Cancel' }}
                                            onClick=${handleCloseAutoPopulate}
                                        />
                                        <${Button}
                                            buttonType="primary"
                                            label=${{ text: 'Auto-Populate' }}
                                            onClick=${handleAutoPopulateConfirm}
                                        />
                                    `}
                                    options=${{
                                        isVisible: showAutoPopulateModal,
                                        onCloseEvent: handleCloseAutoPopulate,
                                        alertType: AlertTypes.warning,
                                        message: {
                                            text: 'This will overwrite existing manual ratings with data from the Evidence Based Approach. Are you sure you want to continue?',
                                        },
                                    }}
                                />
                            </div>
                        `
        }
    `;
};

export default SarbSelfAssessment;
